import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { Export, ReportExportTypeEnum, Overview } from "@motolynk/api-client";
import { formatDateTime, useUiLabels } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DatePicker,
  ErrorState,
  formatMoney,
  FormField,
  Icon,
  KpiTile,
  LoadingState,
  PageHeader,
  RankList,
  Select,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
  TextLink,
  toast,
  TrendChart,
} from "@motolynk/ui";

export interface ReportsSource {
  panel: "admin" | "vendor";
  overview: (params: { from: string; to: string; vendor?: string }) => Promise<Overview>;
  exportsList?: () => Promise<Export[]>;
  exportsCreate?: (data: {
    report: ReportExportTypeEnum;
    date_from: string;
    date_to: string;
  }) => Promise<Export>;
  fileUrl?: (uid: string) => string;
  reports?: readonly ReportExportTypeEnum[];
}

function isoDay(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function lastDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86_400_000);
  return { from: isoDay(from), to: isoDay(to) };
}

const PRESETS = ["7", "30", "90", "custom"] as const;

/** Admin step 1 & 19 / vendor step 4: figures for a period, charts, best sellers, CSV exports. */
export function ReportsView({
  source,
  vendor,
  embedded = false,
}: {
  source: ReportsSource;
  vendor?: string;
  embedded?: boolean;
}) {
  const { t, i18n } = useTranslation("reports");
  const ui = useUiLabels();
  const [preset, setPreset] = useState<(typeof PRESETS)[number]>("30");
  const [range, setRange] = useState(lastDays(30));
  const params = { ...range, ...(vendor ? { vendor } : {}) };
  const data = useQuery({
    queryKey: [source.panel, "reports", "overview", params],
    queryFn: () => source.overview(params),
  });
  const money = (value: string) => formatMoney(value, "INR", i18n.language);
  const report = data.data;
  const day = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
    });
  return (
    <>
      {!embedded && (
        <PageHeader title={t("title")} description={t(`description.${source.panel}`)} />
      )}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Tabs
          value={preset}
          onValueChange={(value) => {
            setPreset(value as (typeof PRESETS)[number]);
            if (value !== "custom") setRange(lastDays(Number(value)));
          }}
        >
          <TabsList>
            {PRESETS.map((value) => (
              <TabsTrigger key={value} value={value}>
                {t(`presets.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {preset === "custom" && (
          <>
            <FormField label={t("from")}>
              <DatePicker
                value={range.from}
                onChange={(value) => setRange({ ...range, from: value })}
              />
            </FormField>
            <FormField label={t("to")}>
              <DatePicker
                value={range.to}
                onChange={(value) => setRange({ ...range, to: value })}
              />
            </FormField>
          </>
        )}
      </div>
      {data.isPending && <LoadingState />}
      {data.isError && (
        <ErrorState
          title={data.error.message || t("loadFailed")}
          retryLabel={ui.retry}
          onRetry={() => void data.refetch()}
        />
      )}
      {report && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiTile
              label={t("kpi.sales")}
              value={money(report.kpis.sales)}
              icon="trending"
              tone="dark"
              hint={t("kpi.average", { amount: money(report.kpis.average_order) })}
            />
            <KpiTile
              label={t("kpi.orders")}
              value={report.kpis.orders}
              icon="inbox"
              hint={t("kpi.units", { count: report.kpis.units })}
            />
            <KpiTile
              label={t("kpi.commission")}
              value={money(report.kpis.commission)}
              icon="tag"
              hint={t("kpi.payable", { amount: money(report.kpis.payable) })}
            />
            <KpiTile
              label={t("kpi.refunded")}
              value={money(report.kpis.refunded)}
              icon="undo"
              hint={t("kpi.cancelled", { count: report.kpis.cancelled })}
            />
            {report.kpis.new_customers !== undefined && (
              <KpiTile
                label={t("kpi.newCustomers")}
                value={report.kpis.new_customers}
                icon="users"
              />
            )}
            {report.kpis.new_vendors !== undefined && (
              <KpiTile label={t("kpi.newVendors")} value={report.kpis.new_vendors} icon="store" />
            )}
            <KpiTile label={t("kpi.paidOut")} value={money(report.kpis.paid_out)} icon="wallet" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("charts.sales")}</CardTitle>
              </CardHeader>
              <CardContent>
                <TrendChart
                  title={t("charts.sales")}
                  labels={{ x: t("charts.day"), y: t("kpi.sales") }}
                  emptyLabel={t("charts.empty")}
                  data={report.series.map((p) => ({
                    label: day(p.date),
                    value: Number(p.sales),
                    display: money(p.sales),
                  }))}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t("charts.orders")}</CardTitle>
              </CardHeader>
              <CardContent>
                <TrendChart
                  title={t("charts.orders")}
                  labels={{ x: t("charts.day"), y: t("kpi.orders") }}
                  emptyLabel={t("charts.empty")}
                  data={report.series.map((p) => ({
                    label: day(p.date),
                    value: p.orders,
                    display: String(p.orders),
                  }))}
                />
              </CardContent>
            </Card>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>{t("lists.products")}</CardTitle>
              </CardHeader>
              <CardContent>
                <RankList
                  title={t("lists.products")}
                  emptyLabel={t("charts.empty")}
                  items={report.top_products.map((p) => ({
                    label: p.title,
                    value: p.units,
                    display: t("lists.units", { count: p.units }),
                    hint: money(p.revenue),
                  }))}
                />
              </CardContent>
            </Card>
            {source.panel === "admin" && !vendor ? (
              <Card>
                <CardHeader>
                  <CardTitle>{t("lists.stores")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RankList
                    title={t("lists.stores")}
                    emptyLabel={t("charts.empty")}
                    items={report.top_vendors.map((v) => ({
                      label: v.store,
                      value: Number(v.sales),
                      display: money(v.sales),
                      hint: t("lists.orders", { count: v.orders }),
                    }))}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>{t("lists.status")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {report.by_status
                    .filter((s) => s.count > 0)
                    .map((s) => (
                      <div key={s.status} className="flex items-center justify-between text-sm">
                        <StatusBadge
                          status={s.status}
                          label={t(`status.${s.status}`, { defaultValue: s.status })}
                        />
                        <span className="font-medium">{s.count}</span>
                      </div>
                    ))}
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle>{t("lists.payments")}</CardTitle>
              </CardHeader>
              <CardContent>
                <RankList
                  title={t("lists.payments")}
                  emptyLabel={t("charts.empty")}
                  items={report.payment_methods.map((m) => ({
                    label: t(`payment.${m.method}`, { defaultValue: m.method }),
                    value: Number(m.sales),
                    display: money(m.sales),
                    hint: t("lists.orders", { count: m.orders }),
                  }))}
                />
              </CardContent>
            </Card>
          </div>
          {source.exportsList && !embedded && <Exports source={source} range={range} />}
        </div>
      )}
    </>
  );
}

function Exports({
  source,
  range,
}: {
  source: ReportsSource;
  range: { from: string; to: string };
}) {
  const { t, i18n } = useTranslation("reports");
  const queryClient = useQueryClient();
  const reports = source.reports ?? [];
  const [report, setReport] = useState<ReportExportTypeEnum>(reports[0] ?? "orders");
  const key = [source.panel, "reports", "exports"];
  const list = useQuery({
    queryKey: key,
    queryFn: () => source.exportsList!(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((row) => row.status === "queued" || row.status === "running")
        ? 3000
        : false,
  });
  const create = useMutation({
    mutationFn: () => source.exportsCreate!({ report, date_from: range.from, date_to: range.to }),
    onSuccess: async () => {
      toast.success(t("exports.queued"));
      await queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : String(error)),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("exports.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <FormField label={t("exports.report")} className="w-64">
            <Select
              value={report}
              onValueChange={(value) => setReport(value as ReportExportTypeEnum)}
              options={reports.map((value) => ({ value, label: t(`exports.reports.${value}`) }))}
            />
          </FormField>
          <Button loading={create.isPending} onClick={() => create.mutate()}>
            {t("exports.create", { from: range.from, to: range.to })}
          </Button>
        </div>
        <ul className="divide-y divide-border text-sm">
          {(list.data ?? []).map((row) => (
            <li key={row.uid} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <span>
                {t(`exports.reports.${row.report}`, { defaultValue: row.report })} ·{" "}
                {String((row.filters as { from?: string }).from)} →{" "}
                {String((row.filters as { to?: string }).to)}
                <span className="block text-xs text-muted-foreground">
                  {formatDateTime(row.created_at, i18n.language)}
                </span>
              </span>
              {row.status === "ready" && source.fileUrl ? (
                <TextLink href={source.fileUrl(row.uid)}>
                  <Icon name="upload" size={14} /> {t("exports.download", { count: row.rows })}
                </TextLink>
              ) : (
                <StatusBadge status={row.status} label={t(`exports.status.${row.status}`)} />
              )}
            </li>
          ))}
          {list.data?.length === 0 && (
            <li className="py-2 text-muted-foreground">{t("exports.none")}</li>
          )}
        </ul>
      </CardContent>
    </Card>
  );
}
